import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Logo } from "../shared/components/Logo";
import { ThemeToggle } from "../shared/components/ThemeToggle";
import { AuthInput } from "../features/auth/components/ui/AuthInput";
import { AuthButton } from "../features/auth/components/ui/AuthButton";
import { WatchmanFetch } from "../shared/utils/watchmanFetch";
import { handleApiError } from "../shared/utils/handleApiError";

export default function ForgotPasswordPage() {
    const [email, setEmail] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [serverError, setServerError] = useState("");

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setServerError("");

        try {
            // Enumeration-safe: backend always returns { message } regardless of
            // whether the email maps to an account.
            await WatchmanFetch.post("/auth/forgot-password", { email });
            setSubmitted(true);
        } catch (error: unknown) {
            setServerError(handleApiError(error));
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <div className="min-h-dvh bg-background text-foreground flex flex-col items-center justify-center px-4 py-10">
            {/* Theme toggle — pinned top-right, reuses the in-app store/mechanism */}
            <div className="absolute top-4 right-4">
                <ThemeToggle />
            </div>

            <div className="w-full max-w-md flex flex-col items-center">
                {/* Prominent brand mark */}
                <Logo className="h-9 w-auto mb-8" />

                {/* Auth card */}
                <div className="w-full rounded-2xl border border-border bg-card text-card-foreground shadow-sm px-6 py-8 sm:px-8">
                    {submitted ? (
                        <div className="space-y-8">
                            <div className="text-center space-y-2">
                                <h1 className="text-[28px] font-bold text-foreground">
                                    Check your email
                                </h1>
                                <p className="text-sm text-muted-foreground">
                                    If an account exists, we&apos;ve emailed you a reset link.
                                </p>
                            </div>

                            <Link
                                to="/login"
                                className="block text-center text-sm text-foreground font-medium hover:underline transition-colors">
                                Back to login
                            </Link>
                        </div>
                    ) : (
                        <div className="space-y-8">
                            <div className="text-center space-y-2">
                                <h1 className="text-[28px] font-bold text-foreground">
                                    Forgot your password?
                                </h1>
                                <p className="text-sm text-muted-foreground">
                                    Enter your email and we&apos;ll send you a link to reset it.
                                </p>
                            </div>

                            <form
                                onSubmit={handleSubmit}
                                className="space-y-6">
                                <AuthInput
                                    name="email"
                                    type="email"
                                    placeholder="Email"
                                    value={email}
                                    onChange={(e) => {
                                        setEmail(e.target.value);
                                        if (serverError) setServerError("");
                                    }}
                                    required
                                />

                                {serverError && (
                                    <div className="text-destructive text-sm font-normal">
                                        {serverError}
                                    </div>
                                )}

                                <AuthButton
                                    type="submit"
                                    loading={isLoading}>
                                    Send reset link
                                </AuthButton>

                                <Link
                                    to="/login"
                                    className="block text-center text-sm text-foreground font-medium hover:underline transition-colors">
                                    Back to login
                                </Link>
                            </form>
                        </div>
                    )}
                </div>

                <p className="mt-6 text-xs text-muted-foreground text-center">
                    The Collective Synergy &middot; Admin
                </p>
            </div>
        </div>
    );
}
